import { lazy, Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { loadAudienceLayerModule } from "@/lib/audienceLayerRemote";
import type { AudienceLayerHostContext } from "../Pages/Watch/VideoPlayerLive/useAudienceLayerHost";
import "./AudienceLayerPanel.css";

/*
 * Loaded at runtime from the separate Audience Layer deploy, and NOT declared
 * in the build config: see lib/audienceLayerRemote for what declaring it there
 * cost every page of this app. A load or runtime failure here is contained
 * below and never reaches the video player.
 */
const AudienceLayer = lazy(() =>
  loadAudienceLayerModule<{
    default: React.ComponentType<{ hostContext?: AudienceLayerHostContext }>;
  }>("App"),
);

type Props = {
  enabled: boolean;
  // Null when nobody is signed in: there is no fan to attribute messages or
  // reactions to, and every request the panel made would 401.
  hostContext?: AudienceLayerHostContext | null;
  // What to show instead, which on the Watch page is the sidebar this panel
  // replaces. Rendered whenever the layer cannot be: disabled for the tenant,
  // signed out, or the remote failed to load. Losing the chat entirely because
  // a separately deployed bundle is unreachable would be a worse outage than
  // the one it is meant to contain.
  fallback?: ReactNode;
};

export function AudienceLayerPanel({ enabled, hostContext, fallback = null }: Props) {
  // Runtime kill switch: when tenant config disables the layer, the remote is
  // neither rendered nor fetched.
  if (!enabled || !hostContext) return <>{fallback}</>;

  return (
    // The <aside> lives INSIDE the ErrorBoundary so a remote failure replaces
    // the whole slot rather than leaving an empty 380px column beside the
    // video. The Suspense skeleton reserves the slot only while loading.
    <ErrorBoundary
      fallbackRender={() => <>{fallback}</>}
      onError={(err) => console.error("[AL] runtime error:", err)}
    >
      <aside className="audience-layer-panel">
        <Suspense fallback={<AudienceLayerSkeleton />}>
          <AudienceLayer hostContext={hostContext} />
        </Suspense>
      </aside>
    </ErrorBoundary>
  );
}

function AudienceLayerSkeleton() {
  return <div className="audience-layer-skeleton" />;
}
